import { utils } from '@kurtosys/ksys-app-template';
import * as FileSaver from 'file-saver';
import { StoreBase } from '../../../common/StoreBase';
import { observable, computed } from 'mobx';
import { IFormatter, IQueryOptions } from '../../../models/commonTypes';
import { ICsvDownloaderOptions, ICsvDownloaderOptionsColumn } from '../models/ICsvDownloaderOptions';

export class DownloadStore extends StoreBase {
	@observable isLoading: boolean = false;
	quote = '\x22';
	newLine = '\r\n';
	separator = '\x2C';

	async initialize() {
		this.isLoading = true;
		const csvText = this.csvText;
		const filename = this.filename;
		await this.downloadCsv(csvText, filename);
		this.logDownloadEvent(filename);
		this.isLoading = false;
	}

	@computed
	get csvDownloaderOptions(): ICsvDownloaderOptions {
		const { appStore } = this.storeContext;
		if (appStore) {
			const { csvDownloaderOptions } = appStore;
			if (csvDownloaderOptions) {
				return csvDownloaderOptions;
			}
		}
		return {
			filename: {
				queryOptionsType: 'none',
				value: 'no options provided',
			},
			columns: [],
			rowsQuery: {
				queryOptionsType: 'none',
			},
		};
	}

	@computed
	get rowsQuery(): IQueryOptions {
		return this.csvDownloaderOptions.rowsQuery;
	}

	@computed
	get columns() {
		return this.csvDownloaderOptions.columns || [];
	}

	@computed
	get currencyKey() {
		return this.csvDownloaderOptions.currencyKey || 'currency';
	}

	@computed
	get rows() {
		return this.getQueryValue(this.rowsQuery) || [];
	}

	@computed
	get rowsAsArrays() {
		const response: (string[])[] = [];
		for (const row of this.rows) {
			const baseFormatter = this.getBaseFormatter(row);
			const rowArray: string[] = [];
			for (const column of this.columns) {
				rowArray.push(this.getValue(row, column, baseFormatter));
			}
			response.push(rowArray);
		}
		return response;
	}

	@computed
	get headerRow() {
		return this.columns.map(column => column.header || column.key);
	}

	@computed
	get csvText() {
		const fileContent = [
			this.headerRow,
			...this.rowsAsArrays,
			...this.disclaimerRows,
		];

		let response = fileContent.map(this.getRowText).join(this.newLine);
		response = this.cleanOutput(response);
		return response;
	}

	@computed
	get disclaimerRows() {
		const response: (string[])[] = [];
		for (const disclaimerQuery of this.disclaimerQueries) {
			if (disclaimerQuery) {
				const disclaimer = this.getQueryValue(disclaimerQuery);
				if (disclaimer) {
					response.push(
						[disclaimer],
					);
				}
			}
		}
		return response;
	}

	@computed
	get disclaimerQueries() {
		return this.csvDownloaderOptions.disclaimers || [];
	}

	cleanOutput(response: string) {
		const charsToReplace = [
			{
				source: ' ',
				replacement: ' ',
			},
		];
		charsToReplace.forEach((charToReplace) => {
			const { source, replacement } = charToReplace;
			response = response.split(source).join(replacement);
		});
		response = response.replace(/<[^>]*>?/gm, '');
		return response;
	}

	getRowText = (row: string[]) => {
		return row.map(this.getCellText).join(this.separator);
	}

	getCellText = (value: string) => {
		return `${ this.quote }${ value.split(this.quote).join(`${ this.quote }${ this.quote }`) }${ this.quote }`;
	}

	@computed
	get filename() {
		const filenameQuery = this.csvDownloaderOptions.filename;
		const base = this.getQueryValue(filenameQuery);
		if (base && typeof base === 'string') {
			return `${ base }${ !base.endsWith('.csv') ? '.csv' : '' }`;
		}
		return 'data-download.csv';
	}

	getBaseFormatter = (row: object[]): Partial<IFormatter> => {
		const { appStore } = this.storeContext;
		const currency = utils.get(row, this.currencyKey);
		return {
			currency,
			locale: appStore.getInput('culture'),
			options: {
				currency,
			},
		};
	}

	getValue = (row: object, column: ICsvDownloaderOptionsColumn, baseFormatter: Partial<IFormatter>) => {
		const { key, format } = column;
		const rawValue = utils.get(row, key);
		let response = rawValue;
		if (format) {
			const workingFormat = utils.object.deepMergeObjects(baseFormatter, format);
			response = utils.formatter.formatter(workingFormat, rawValue);
		}
		return !utils.typeChecks.isNullOrUndefined(response) ? response.toString() : '-';
	}

	downloadCsv = async (csv: string, filename: string) => {
		const BOM = "\uFEFF";
		const csvData = BOM + csv;
		try {

			const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8' });
			FileSaver.saveAs(blob, filename);
		}
		catch (e) {
			console.warn(e);
		}
	}

	logDownloadEvent = (filename: string): void => {
		const { appStore } = this.storeContext;
		if (appStore) {
			const { analyticsHelper } = appStore;
			if (analyticsHelper) {
				analyticsHelper.logEvent({
					event: 'button_click',
					eventType: 'click',
					context: { event: 'File download', filename: filename},
				});
			}
		}
	}
}

